/**
 * Formação de custos da entrada de mercadoria — espelho do servidor.
 *
 * Existe para a tela recalcular AO VIVO enquanto o operador mexe em frete,
 * despesas e desconto. Quem grava é o backend (`XmlLandedCost`), que refaz a
 * mesma conta no confirmar; isto aqui é pré-visualização.
 *
 * Por isso a matemática é idêntica linha a linha, e há teste de paridade com os
 * mesmos casos do PHP: divergir significaria a tela prometer um custo e o banco
 * guardar outro — o operador decide a margem olhando um número que não é o que
 * vai valer.
 */

/** `round()` half-up de 2 casas, igual ao PHP. */
export const round2 = (n) => Math.round((Number(n) || 0) * 100 + Number.EPSILON) / 100;

/** Desconto do cabeçalho em reais, venha ele em R$ ou em %. */
export function descontoEmReais({ desconto = 0, tipo = "fixed", baseTotal = 0 }) {
  const v = Number(desconto) || 0;
  if (tipo !== "percentage") {
    // Desconto maior que a mercadoria zeraria o custo e viraria crédito.
    return round2(Math.min(v, baseTotal));
  }

  return round2((baseTotal * Math.min(100, Math.max(0, v))) / 100);
}

/**
 * Espalha um valor do cabeçalho entre os itens que não o trazem próprio.
 *
 * O último centavo vai para a maior linha, senão a soma dos rateios arredondados
 * não fecha com o cabeçalho — e a diferença aparece como um custo que não bate
 * com o documento.
 */
function distribuir(items, campo, totalNota, baseTotal) {
  const jaTem = items.reduce((s, i) => s + (Number(i[campo]) || 0), 0);
  if (jaTem > 0 || totalNota <= 0 || baseTotal <= 0) return items;

  let distribuido = 0;
  let maior = 0;
  const saida = items.map((it, k) => {
    const parte = round2(totalNota * ((Number(it.v_prod) || 0) / baseTotal));
    distribuido += parte;
    if ((Number(it.v_prod) || 0) > (Number(items[maior].v_prod) || 0)) maior = k;

    return { ...it, [campo]: parte };
  });

  const sobra = round2(totalNota - distribuido);
  if (Math.abs(sobra) >= 0.01) {
    saida[maior] = { ...saida[maior], [campo]: round2(saida[maior][campo] + sobra) };
  }

  return saida;
}

/**
 * Aplica o cabeçalho aos itens e devolve custo real por unidade e a composição.
 *
 * @param items   itens com v_prod, qty e acessórios próprios (v_frete etc.)
 * @param totals  { frete, seguro, outros, desconto }
 * @param opts    { discount_type: "fixed" | "percentage" }
 */
export function applyPricing(items = [], totals = {}, opts = {}) {
  const baseTotal = items.reduce((s, i) => s + (Number(i.v_prod) || 0), 0);

  const cabecalho = {
    ...totals,
    desconto: descontoEmReais({
      desconto: totals.desconto,
      tipo: opts.discount_type,
      baseTotal,
    }),
  };

  let saida = items;
  for (const [chave, campo] of [["frete", "v_frete"], ["seguro", "v_seg"], ["outros", "v_outro"], ["desconto", "v_desc"]]) {
    saida = distribuir(saida, campo, Number(cabecalho[chave]) || 0, baseTotal);
  }

  return saida.map((it) => {
    const qty = Math.max(1, parseInt(it.qty, 10) || 1);
    const extra = (Number(it.v_frete) || 0) + (Number(it.v_seg) || 0) + (Number(it.v_outro) || 0);
    // IPI e ICMS-ST são pagos na compra e não voltam para quem revende no
    // Simples: entram no custo como qualquer despesa.
    const acessorios = extra + (Number(it.v_ipi) || 0) + (Number(it.v_icms_st) || 0) - (Number(it.v_desc) || 0);
    const total = round2((Number(it.v_prod) || 0) + acessorios);

    return {
      ...it,
      landed_total: total,
      unit_cost: round2(total / qty),
      allocated_extra: round2(extra),
      allocated_discount: round2(Number(it.v_desc) || 0),
      cost_parts: {
        produto: round2(it.v_prod),
        frete: round2(it.v_frete),
        seguro: round2(it.v_seg),
        outros: round2(it.v_outro),
        ipi: round2(it.v_ipi),
        icms_st: round2(it.v_icms_st),
        desconto: round2(it.v_desc),
      },
    };
  });
}

/**
 * Preço de venda a partir da margem SOBRE O CUSTO.
 *
 * `custo × (1 + margem/100)` — é como o lojista negocia ("faço 80% em cima").
 * Não confundir com margem sobre a venda, que é o número menor e aparece no
 * cadastro de produto como "% do preço".
 */
export const precoComMargem = (custo, margem) => round2((Number(custo) || 0) * (1 + (Number(margem) || 0) / 100));

/** Caminho inverso: preço digitado à mão vira a margem que ele representa. */
export const margemDoPreco = (custo, preco) => {
  const c = Number(custo) || 0;

  return c <= 0 ? 0 : round2(((Number(preco) || 0) / c - 1) * 100);
};

/** Somatórios do rodapé — servem para provar que o rateio fecha. */
export function totaisRateio(items) {
  const soma = (campo) => round2(items.reduce((s, i) => s + (Number(i[campo]) || 0), 0));

  return {
    base: soma("v_prod"),
    extra: soma("allocated_extra"),
    desconto: soma("allocated_discount"),
    final: soma("landed_total"),
  };
}
